import React, { useMemo, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { App, Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import {
  ClipboardList,
  Link2,
  Mail,
  Clock,
  MessageSquare,
  Trash2,
  MoreHorizontal,
} from 'lucide-react'
import { getAppBaseUrl } from '../../utils/appUrl'
import { formatRelativeShort } from '../../utils/formatRelativeTime'
import type { ResearchItem } from './types'
import {
  ResearchRow,
  RowAccent,
  RowIconBadge,
  RowMain,
  RowTitleLine,
  RowTitle,
  RowTypeLabel,
  RowStatusLine,
  RowStatusChip,
  RowStatusItem,
  RowStatusSep,
  RowMetric,
  RowMetricNumber,
  RowMetricLabel,
  RowActions,
  RowActionButton,
  RowMoreButton,
} from './ResearchPageStyles'

interface SurveyCardProps {
  item: ResearchItem
  onDelete: (item: ResearchItem) => void
}

export const SurveyCard = React.memo(({ item, onDelete }: SurveyCardProps) => {
  const { notification } = App.useApp()
  const navigate = useNavigate()
  const isLive = item.status === 'active'

  const handleCardClick = useCallback(() => {
    void navigate(`/survey/${item.id}/insights`)
  }, [item.id, navigate])

  const handleCopyLink = useCallback(async () => {
    const url = `${getAppBaseUrl()}/s/${item.id}`
    try {
      await navigator.clipboard.writeText(url)
      notification.success({ title: 'Link copied' })
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'research', action: 'copy_survey_link' },
      })
      notification.error({ title: 'Could not copy link' })
    }
  }, [item.id, notification])

  const handleSendByEmail = useCallback(() => {
    const url = `${getAppBaseUrl()}/s/${item.id}`
    const topic = item.parentLabel ? `"${item.parentLabel}"` : "an idea I'm working on"
    const subject = encodeURIComponent(
      item.parentLabel ? `Quick survey: ${item.parentLabel}` : 'Quick survey — your opinion matters'
    )
    const body = encodeURIComponent(
      `Hi,\n\nI'm gathering feedback on ${topic} and would love to hear your perspective.\n\nI put together a short survey — it takes about ${item.estimatedMinutes} minute${item.estimatedMinutes !== 1 ? 's' : ''} to complete:\n\n${url}\n\nYour honest input would be really valuable. Feel free to pass this along to anyone else who might have useful thoughts.\n\nThank you!`
    )
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank')
  }, [item])

  const shareMenuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'copy-link',
        icon: <Link2 size={13} />,
        label: 'Copy link',
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation()
          void handleCopyLink()
        },
      },
      {
        key: 'send-email',
        icon: <Mail size={13} />,
        label: 'Send by email',
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation()
          handleSendByEmail()
        },
      },
    ],
    [handleCopyLink, handleSendByEmail]
  )

  const moreMenuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'delete',
        icon: <Trash2 size={13} />,
        label: 'Delete survey',
        danger: true,
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation()
          onDelete(item)
        },
      },
    ],
    [item, onDelete]
  )

  const timestamp = isLive
    ? `Created ${formatRelativeShort(item.createdAt, true)}`
    : item.closedAt
      ? `Ended ${formatRelativeShort(item.closedAt, true)}`
      : 'Closed'

  return (
    <ResearchRow onClick={handleCardClick}>
      <RowAccent $live={isLive} />
      <RowIconBadge $live={isLive}>
        <ClipboardList size={16} />
      </RowIconBadge>
      <RowMain>
        <RowTitleLine>
          <RowTitle title={item.label}>{item.label || 'Untitled survey'}</RowTitle>
          <RowTypeLabel>Survey</RowTypeLabel>
        </RowTitleLine>
        <RowStatusLine>
          <RowStatusChip $status={isLive ? 'live' : 'closed'}>
            {isLive ? 'Live' : 'Closed'}
          </RowStatusChip>
          <RowStatusSep />
          <RowStatusItem>
            <MessageSquare size={11} />
            {item.questionCount} question{item.questionCount !== 1 ? 's' : ''}
          </RowStatusItem>
          <RowStatusSep />
          <RowStatusItem>
            <Clock size={11} />~{item.estimatedMinutes}m
          </RowStatusItem>
          <RowStatusSep />
          <RowStatusItem>{timestamp}</RowStatusItem>
        </RowStatusLine>
      </RowMain>
      <RowMetric>
        <RowMetricNumber>{item.respondentCount}</RowMetricNumber>
        <RowMetricLabel>response{item.respondentCount !== 1 ? 's' : ''}</RowMetricLabel>
      </RowMetric>
      <RowActions onClick={(e) => e.stopPropagation()}>
        <Dropdown menu={{ items: shareMenuItems }} trigger={['click']} placement="bottomRight">
          <RowActionButton type="button">
            <Link2 size={13} />
            Share
          </RowActionButton>
        </Dropdown>
        <Dropdown menu={{ items: moreMenuItems }} trigger={['click']} placement="bottomRight">
          <RowMoreButton type="button" title="More actions">
            <MoreHorizontal size={16} />
          </RowMoreButton>
        </Dropdown>
      </RowActions>
    </ResearchRow>
  )
})

SurveyCard.displayName = 'SurveyCard'
